import { useMemo } from "react";
import {
  GfImageWithUsageCount,
  ImageSearchSortBehaviour,
} from "@swagger/typescript-fetch-goflyer";
import { useImageSearchSortMode } from "./useImageSearchSortMode";

interface UseSortedGfImageResultsParams {
  itemsList?: GfImageWithUsageCount[];
  searchText?: string;
}

const normalizeText = (value?: string) => (value || "").trim().toLowerCase();

const getSimpleRelevanceScore = (
  item: GfImageWithUsageCount,
  query: string,
): number => {
  if (!query) return 0;

  const text = normalizeText(item.alt);
  if (!text) return 0;

  if (text === query) return 4;
  if (text.startsWith(query)) return 3;
  if (text.includes(query)) return 2;

  const queryWords = query.split(/\s+/).filter(Boolean);
  if (
    queryWords.length > 0 &&
    queryWords.every((word) => text.includes(word))
  ) {
    return 1;
  }

  return 0;
};

/**
 * Sorts a list of images based on the active admin sort-mode setting.
 *
 * Items are first partitioned into two groups:
 *  - **With usage**  (`flyerItemUsageCount > 0`) — sorted according to the active mode, displayed first.
 *  - **Without usage** (`flyerItemUsageCount` is null / 0) — kept in original backend order, displayed after.
 *
 * ### Sort modes (`ImageSearchSortMode = ImageSearchSortBehaviour | null`)
 * Controlled by admin user settings → category: `SettingCategory.ImageSearch`,
 * key: `SettingKey.ImageSearchUsageSortBehaviour`.
 *
 * **`null`** (no active setting — default, treated as usage-first)
 *   1. Highest `flyerItemUsageCount` first
 *   2. Higher relevance score as tie-breaker
 *   3. Original backend index as final tie-breaker
 *
 * **`ImageSearchSortBehaviour.UsageCountFirst`** (`'usage_count_first'`)
 *   1. Highest `flyerItemUsageCount` first
 *   2. Higher relevance score as tie-breaker
 *   3. Original backend index as final tie-breaker
 *
 * **`ImageSearchSortBehaviour.RelevanceFirst`** (`'relevance_first'`)
 *   1. Highest relevance score first (exact match → starts-with → contains → all words → no match)
 *   2. Higher `flyerItemUsageCount` as tie-breaker
 *   3. Original backend index as final tie-breaker
 *
 * Relevance is scored against `item.alt` using the normalised `searchText`:
 *  - `4` exact match, `3` starts-with, `2` contains, `1` all words present, `0` no match.
 */
export const useSortedGfImageResults = ({
  itemsList,
  searchText = "",
}: UseSortedGfImageResultsParams) => {
  const sortMode = useImageSearchSortMode();

  console.log("sortMode,", sortMode);
  return useMemo(() => {
    if (!itemsList || itemsList.length === 0) {
      return itemsList;
    }

    const normalizedQuery = normalizeText(searchText);

    // Split into items that have a usage count and those that don't.
    // Items without flyerItemUsageCount are left untouched (original order).
    const withUsageMeta = itemsList
      .map((item, index) => ({
        item,
        index,
        usageCount: item.flyerItemUsageCount!,
        relevanceScore: getSimpleRelevanceScore(item, normalizedQuery),
      }))
      .filter((m) => (m.item.flyerItemUsageCount ?? 0) > 0);

    const withoutUsage = itemsList.filter(
      (item) => (item.flyerItemUsageCount ?? 0) <= 0,
    );

    if (
      sortMode === null ||
      sortMode === ImageSearchSortBehaviour.UsageCountFirst
    ) {
      const sorted = withUsageMeta
        .sort((a, b) => {
          // Usage-first: highest count is primary
          if (a.usageCount !== b.usageCount) {
            return b.usageCount - a.usageCount;
          }
          if (a.relevanceScore !== b.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          return a.index - b.index;
        })
        .map(({ item }) => item);
      return [...sorted, ...withoutUsage];
    }

    // Relevance mode: text relevance first, usage as fallback.
    // Only items with a usage count are sorted; the rest follow unchanged.
    const sorted = withUsageMeta
      .sort((a, b) => {
        if (a.relevanceScore !== b.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }
        if (a.usageCount !== b.usageCount) {
          return b.usageCount - a.usageCount;
        }
        return a.index - b.index;
      })
      .map(({ item }) => item);
    return [...sorted, ...withoutUsage];
  }, [itemsList, sortMode, searchText]);
};
